import { Theme } from "@mui/material";
import { ActionPlanSummary } from "@mutiny-group/sbo-openapi-client";
import { useApproveScenarioForActionPlan } from "../../../api/v2/approveScenarioForActionPlan";
import { useQueryClient } from "@tanstack/react-query";
import { ScenarioOption } from "../../../components/NewActionPlanModal/types";
import { useScenariosV3Routes } from "../../../hooks/useScenariosV3Routes";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { Location } from "history";

export const BannerStyleMapping = (theme: Theme) => ({
  approved: {
    title: "This scenario is being tracked in an Action Plan",
    description:
      "View the Action Plan for updates on progress and recommendations about how to adjust your strategy to achieve your revenue goals.",
    style: {
      banner: {
        backgroundColor: theme.palette.infoBanner.lightBackground,
      },
      infoIcon: {
        color: theme.palette.mutinexMagenta.main,
      },
    },
  },
  approveMany: {
    title: "Ready to take action?",
    description:
      "Track this scenario in an Action Plan to get updates on progress and recommendations about how to achieve your revenue goals. Create a new Action Plan, or select an existing eligible Action Plan from the list below ",
    style: {
      banner: {
        backgroundColor: theme.palette.infoBanner.background,
      },
      infoIcon: {
        color: theme.palette.infoIcon.color,
      },
    },
  },
  approve: {
    title: "Ready to take action?",
    description:
      "Approve this scenario to track it in your Action Plan. You'll get updates on progress and recommendations about how to achieve your revenue goals.",
    style: {
      banner: {
        backgroundColor: theme.palette.infoBanner.background,
      },
      infoIcon: {
        color: theme.palette.infoIcon.color,
      },
    },
  },
  create: {
    title: "Ready to take action?",
    description:
      "Turn this scenario into an Action Plan so you can track your progress and see how you're progressing towards reaching your revenue goals.",
    style: {
      banner: {
        backgroundColor: theme.palette.infoBanner.background,
      },
      infoIcon: {
        color: theme.palette.infoIcon.color,
      },
    },
  },
});

type UseScenarioActionBannerProps = {
  theme: Theme;
  scenarioOption: ScenarioOption;
  eligibleActionPlans: ActionPlanSummary[];
  approvedActionPlans: ActionPlanSummary[];
  setApproveModalOpen: (open: boolean) => void;
};

const getDefaultActionPlan = (eligibleActionPlans: ActionPlanSummary[], location: Location) => {
  if (eligibleActionPlans.length === 1) {
    return eligibleActionPlans[0];
  }
  const actionPlanUuid = (location.state as { actionPlanUuid?: string })?.actionPlanUuid;
  if (actionPlanUuid) {
    return eligibleActionPlans.find((actionPlan) => actionPlan.uuid === actionPlanUuid) ?? null;
  }
  return null;
};

export const useScenarioActionBanner = ({
  theme,
  scenarioOption,
  eligibleActionPlans,
  approvedActionPlans,
  setApproveModalOpen,
}: UseScenarioActionBannerProps) => {
  const queryClient = useQueryClient();
  const { navigateToActionPlan } = useScenariosV3Routes();
  const location = useLocation();

  // Use the first eligible action plan if there is only one, or if the user has come from an action plan page, attempt
  // to pre-select the action plan that the user was on.
  const defaultActionPlan = useMemo(
    () => getDefaultActionPlan(eligibleActionPlans, location),
    [eligibleActionPlans, location]
  );

  const [eligibleActionPlan, setEligibleActionPlan] = useState<ActionPlanSummary | null>(
    defaultActionPlan ?? null
  );

  const bannerState = useMemo(() => {
    if (approvedActionPlans?.length) return "approved";
    if (eligibleActionPlans?.length === 1) return "approve";
    if (eligibleActionPlans?.length > 1) return "approveMany";
    return "create";
  }, [eligibleActionPlans, approvedActionPlans]);

  // Use the first approved action plan
  const approvedActionPlan = approvedActionPlans?.[0] ?? null;

  const approveScenarioForActionPlan = useApproveScenarioForActionPlan({
    config: {
      onSuccess: async ({ uuid: actionPlanUuid }: ActionPlanSummary) => {
        toast.success("Scenario approved successfully!");
        if (actionPlanUuid) {
          await queryClient.invalidateQueries({ queryKey: ["action-plan", actionPlanUuid] });
        }
        await queryClient.invalidateQueries({ queryKey: ["action-plans"] });
        await queryClient.invalidateQueries({ queryKey: ["scenario-v2"] });
        await queryClient.invalidateQueries({ queryKey: ["scenario-results-v2"] });
        setApproveModalOpen(false);
      },
    },
  });

  const handleApproveScenario = () => {
    if (scenarioOption?.uuid && eligibleActionPlan?.uuid) {
      approveScenarioForActionPlan.mutate({
        actionPlanUuid: eligibleActionPlan.uuid,
        scenarioId: scenarioOption.uuid,
      });
    }
  };

  const bannerMapping = BannerStyleMapping(theme);

  return {
    bannerStyle: bannerMapping[bannerState as keyof typeof bannerMapping],
    bannerState,
    handleApproveScenario,
    setEligibleActionPlan,
    navigateToActionPlan,
    isApprovePending: approveScenarioForActionPlan.isPending,
    eligibleActionPlan,
    approvedActionPlan,
  };
};
